/**
 * AR Vision Demo — `useArAgentPage` shared nav/action helper.
 *
 * THROWAWAY DEMO CODE. Every fan-out AR demo page (T2–T9) calls this ONE hook
 * to register its demo frontend tools with Nami consistently. It is a thin,
 * opinionated wrapper over the real `useFrontendTool` + `useAgentModule`
 * plumbing so the fan-out tickets don't each re-derive the registration dance.
 *
 * What it does:
 *  1. Builds `FrontendToolDefinition`s from the page's declared tools (each is a
 *     name + description + zod params + execute()), exactly like the real
 *     `useFrontendTool`.
 *  2. Registers them as the page's agent module via `useAgentModule`, layering
 *     in a sensible AR-demo default header.
 *  3. Registers them with the demo-script registry (see `demo-script.ts`) so
 *     the chat-input interceptor can invoke a tool by name when a scripted
 *     prompt fires.
 *
 * A page typically registers a single nav tool (navigate to itself) plus zero
 * or more action tools (apply credit hold, approve rebuttal, …). Example:
 *
 *   const tools = useMemo<ArAgentTool[]>(() => [
 *     {
 *       name: 'navigate_to_invoice',
 *       description: 'Open the invoice detail for the just-sent order',
 *       parameters: z.object({}),
 *       execute: () =>
 *         navigate({
 *           to: paths.authenticated.accountsReceivable.invoice,
 *           params: { invoiceId },
 *         }),
 *     },
 *   ], [navigate, invoiceId]);
 *
 *   useArAgentPage({ pageId: 'ar-invoice-detail', tools });
 *
 * IMPORTANT: pass a STABLE `tools` array (memoize it) — `useAgentModule`
 * re-registers when the array identity changes.
 */

import { useMemo } from 'react';
import { z } from 'zod';
import type { ModuleKey, PageKey } from '@erp/rpc';
import { useAgentModule } from '@/hooks/useAgentModule';
import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';
import { registerArDemoTools } from './demo-script';

/** One demo tool a page wants Nami to be able to invoke. */
export interface ArAgentTool<T extends z.ZodType = z.ZodType> {
  /** Unique tool name (also the key the demo script pins a prompt to). */
  name: string;
  description: string;
  /** Zod schema for the tool's args (use `z.object({})` for no-arg tools). */
  parameters: T;
  /** The real client-side handler — usually a `navigate()` or a store update. */
  execute: (args: z.infer<T>) => unknown | Promise<unknown>;
  /** Block the agent on the result (rare for nav tools). Defaults false. */
  awaitable?: boolean;
}

export interface UseArAgentPageConfig {
  /**
   * Stable identifier for this page, used as the agent `currentPage` and as the
   * demo-script lookup namespace. e.g. 'ar-invoice-detail', 'ar-summary'.
   */
  pageId: string;
  /** The page's demo tools. MUST be a stable array (memoize it). */
  tools: ArAgentTool[];
  /** Optional chat panel header title (defaults to "Nami — AR"). */
  headerTitle?: string;
  /** Optional chat input placeholder. */
  placeholder?: string;
  /**
   * Module key sent to the agent backend. The demo never reaches the backend
   * (the script interceptor short-circuits), so any valid enum value works;
   * defaults to 'general'. Override only if a page needs it.
   */
  moduleKey?: ModuleKey;
  /** Optional page key sent to the agent backend. */
  pageKey?: PageKey;
}

export interface UseArAgentPageResult {
  /** The built tool definitions, also registered for the interceptor. */
  tools: FrontendToolDefinition[];
}

/**
 * The module key the demo uses. The interceptor bypasses the backend so this is
 * never actually consumed server-side; it just needs to be a valid enum member
 * so the existing plumbing accepts it. 'general' is the safe default.
 */
const DEMO_MODULE_KEY = 'general' as ModuleKey;

export function useArAgentPage(
  config: UseArAgentPageConfig,
): UseArAgentPageResult {
  // Build a FrontendToolDefinition per declared tool. Mirrors useFrontendTool's
  // output (it's just a memo wrapper), built in a map here. Memoized on the
  // tools array identity — callers must pass a stable array per the contract.
  const builtTools = useMemo<FrontendToolDefinition[]>(
    () => config.tools.map(buildFrontendToolDefinition),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.tools],
  );

  // Register the tools both with the live agent module (so real Nami could call
  // them too) and with the demo-script registry (so the interceptor can invoke
  // them by name for a scripted prompt).
  useAgentModule({
    moduleKey: config.moduleKey ?? DEMO_MODULE_KEY,
    pageKey: config.pageKey,
    currentPage: config.pageId,
    tools: builtTools,
    headerTitle: config.headerTitle ?? 'Nami — AR',
    placeholder: config.placeholder,
  });

  useMemo(() => {
    registerArDemoTools(config.pageId, builtTools);
  }, [config.pageId, builtTools]);

  return { tools: builtTools };
}

/**
 * Mirror of `useFrontendTool`'s output, callable inside a `.map` (outside the
 * Rules-of-Hooks constraint that the real hook is bound by). Behaviourally
 * identical — the real hook is just a memo wrapper around this shape.
 */
function buildFrontendToolDefinition(
  tool: ArAgentTool,
): FrontendToolDefinition {
  const awaitable = tool.awaitable ?? false;
  return {
    name: tool.name,
    description: tool.description,
    awaitable,
    schema: {
      name: tool.name,
      description: tool.description,
      parameters: z.toJSONSchema(tool.parameters) as Record<string, unknown>,
      awaitable,
    },
    execute: (args) => tool.execute(args as never),
  };
}
